import { parse, stringify } from "qs";
import { isEmpty, isPlainObject, isEqual } from "lodash";
import { omitDeepBy } from "@franchises/lodash-ext";
import React, { Component, useContext, createContext } from "react";

type QueryParamsType = qs.ParsedQs;

export type QueryParamsState = {
  queryParams: QueryParamsType;
  setQueryParams: (queryParams: QueryParamsType) => void;
  pushQueryParams: (queryParams: QueryParamsType) => void;
};

type Props = { search: string };

const QueryParamsContext = createContext<QueryParamsState>({
  queryParams: {},
  setQueryParams: (_queryParams: QueryParamsType) => {},
  pushQueryParams: (_queryParams: QueryParamsType) => {},
});

export const QueryParamsConsumer = QueryParamsContext.Consumer;

function pushHistoryState(params: QueryParamsType) {
  const newUrl = `?${stringify(params, { encode: false })}`;
  window.history.pushState(null, document.title, newUrl);
}

function isQsValueEmpty(value: unknown): boolean {
  if (typeof value === "string") {
    return /^\s*$/.test(value); // empty string
  } else if (value == null) {
    return true;
  } else if (isPlainObject(value)) {
    return isEmpty(value);
  }

  return false;
}

function omitEmptyQueryValues(params: QueryParamsType) {
  return omitDeepBy(params, isQsValueEmpty) as QueryParamsType;
}

export class QueryParamsProvider extends Component<Props, QueryParamsState> {
  constructor(props: Props) {
    super(props);

    const parsedSearch = parse(props.search, { ignoreQueryPrefix: true });
    const queryParams = omitEmptyQueryValues(parsedSearch);

    this.state = {
      queryParams,
      setQueryParams: this.setQueryParams,
      pushQueryParams: this.pushQueryParams,
    };
  }

  setQueryParams = (inputParams: QueryParamsType) => {
    this.setQueryParamsState(inputParams);
  };

  pushQueryParams = (inputParams: QueryParamsType) => {
    const oldParams = this.state.queryParams;
    const params = { ...oldParams, ...inputParams } as QueryParamsType;

    this.setQueryParamsState(params);
  };

  setQueryParamsState = (inputParams: QueryParamsType) => {
    const params = omitEmptyQueryValues(inputParams);

    // Do not push an update if the params are equal
    if (isEqual(params, this.state.queryParams)) {
      return;
    }

    this.setState(
      {
        queryParams: params,
        setQueryParams: this.setQueryParams,
        pushQueryParams: this.pushQueryParams,
      },
      () => pushHistoryState(params)
    );
  };

  render() {
    return (
      <QueryParamsContext.Provider
        value={this.state}
        children={this.props.children}
      />
    );
  }
}

export function useQueryParams() {
  return useContext(QueryParamsContext);
}
